<script setup>
import { ref, computed, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { X, Send, HelpCircle, CheckCircle2, AlertCircle } from 'lucide-vue-next'
import axios from 'axios'

/**
 * The help panel does not lock the page.
 *
 * It used to, because it was built from the same parts as the dialogs. But it
 * is not a dialog: it is a small panel in the corner, it covers nothing the
 * reader was looking at, and there is no decision it is waiting on. Locking the
 * page meant somebody who opened it to ask about the thing they were reading
 * could no longer scroll to the thing they were reading.
 */
const props = defineProps({
  open: { type: Boolean, default: false },
})

const emit = defineEmits(['close'])

const page = usePage()
const user = computed(() => page.props.auth?.user)
const isGuest = computed(() => !user.value)
const supportEmail = computed(() => page.props.config?.support_email)

const name = ref('')
const email = ref('')
const message = ref('')
const sending = ref(false)
const sent = ref(false)
const error = ref('')
const validationError = ref('')

const replyToEmail = computed(() => user.value?.email || email.value)

const canSubmit = computed(() => {
  if (sending.value || message.value.trim().length < 10) return false
  if (isGuest.value && (name.value.trim().length === 0 || email.value.trim().length === 0)) return false
  return true
})

const validate = () => {
  if (isGuest.value && name.value.trim().length === 0) {
    validationError.value = 'Please tell us your name.'
    return false
  }
  if (isGuest.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
    validationError.value = 'Please enter a valid email address.'
    return false
  }
  if (message.value.trim().length === 0) {
    validationError.value = 'Please enter a message.'
    return false
  }
  if (message.value.trim().length < 10) {
    validationError.value = 'Message must be at least 10 characters.'
    return false
  }
  validationError.value = ''
  return true
}

const submit = async () => {
  if (!validate()) return

  sending.value = true
  error.value = ''

  try {
    await axios.post('/support', {
      message: message.value.trim(),
      ...(isGuest.value ? { name: name.value.trim(), email: email.value.trim() } : {}),
    })
    sent.value = true
    message.value = ''
  } catch (e) {
    if (e.response?.status === 422) {
      const errors = e.response.data.errors
      error.value = errors?.name?.[0] || errors?.email?.[0] || errors?.message?.[0] || 'Validation failed. Please check your input.'
    } else if (e.response?.status === 429) {
      error.value = 'Too many messages sent. Please wait a moment and try again.'
    } else {
      error.value = 'Something went wrong. Please try again.'
    }
  } finally {
    sending.value = false
  }
}

const close = () => {
  emit('close')
  // Reset after animation
  setTimeout(() => {
    sent.value = false
    error.value = ''
    validationError.value = ''
    message.value = ''
    name.value = ''
    email.value = ''
  }, 300)
}

watch(() => message.value, () => {
  if (validationError.value) {
    validationError.value = ''
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-4 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-4 scale-95"
    >
      <div
        v-if="open"
        class="fixed bottom-6 left-6 z-50 w-96 max-w-[calc(100vw-3rem)] rounded-2xl bg-white shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
      >
        <!-- Header -->
        <div class="bg-gradient-to-r from-teal-500 to-teal-800 px-5 py-4 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
              <HelpCircle class="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 class="text-white font-semibold text-sm">Help & Support</h3>
              <p class="text-white/70 text-xs">We typically reply within 24 hours</p>
            </div>
          </div>
          <button
            @click="close"
            class="text-white/70 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
          >
            <X class="w-5 h-5" />
          </button>
        </div>

        <!-- Body -->
        <div class="p-5">
          <!-- Success State -->
          <div v-if="sent" class="text-center py-4">
            <div class="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 class="w-7 h-7 text-green-600" />
            </div>
            <h4 class="font-semibold text-gray-900 mb-1">Message Sent!</h4>
            <p class="text-sm text-gray-500 mb-4">
              We'll get back to you at <span class="font-medium text-gray-700">{{ replyToEmail }}</span>
            </p>
            <button
              @click="close"
              class="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Close
            </button>
          </div>

          <!-- Form State -->
          <template v-else>
            <div class="mb-4">
              <p class="text-sm text-gray-600">
                To: <span class="font-medium text-gray-900">{{ supportEmail }}</span>
              </p>
            </div>

            <div v-if="isGuest" class="grid gap-3 mb-3">
              <div>
                <label for="support-name" class="block text-sm font-medium text-gray-700 mb-1.5">
                  Your name
                </label>
                <input
                  id="support-name"
                  v-model="name"
                  type="text"
                  autocomplete="name"
                  placeholder="Jane Doe"
                  class="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label for="support-email" class="block text-sm font-medium text-gray-700 mb-1.5">
                  Your email
                </label>
                <input
                  id="support-email"
                  v-model="email"
                  type="email"
                  autocomplete="email"
                  placeholder="jane@example.com"
                  class="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div class="mb-1">
              <label for="support-message" class="block text-sm font-medium text-gray-700 mb-1.5">
                How can we help?
              </label>
              <textarea
                id="support-message"
                v-model="message"
                rows="4"
                placeholder="Describe your issue or question..."
                class="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                :class="{ 'border-red-400 focus:ring-red-500': validationError || error }"
                @keydown.meta.enter="submit"
                @keydown.ctrl.enter="submit"
              />
            </div>

            <!-- Validation Error -->
            <div v-if="validationError" class="flex items-center gap-1.5 mb-3">
              <AlertCircle class="w-3.5 h-3.5 text-red-500 shrink-0" />
              <p class="text-xs text-red-600">{{ validationError }}</p>
            </div>

            <!-- Server Error -->
            <div v-else-if="error" class="flex items-center gap-1.5 mb-3">
              <AlertCircle class="w-3.5 h-3.5 text-red-500 shrink-0" />
              <p class="text-xs text-red-600">{{ error }}</p>
            </div>

            <div v-else class="mb-3" />

            <!-- Submit -->
            <button
              @click="submit"
              :disabled="!canSubmit"
              class="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium text-sm py-2.5 px-4 rounded-lg transition-colors"
            >
              <Send class="w-4 h-4" />
              <span>{{ sending ? 'Sending...' : 'Send Message' }}</span>
            </button>

            <p v-if="!isGuest" class="text-xs text-gray-400 text-center mt-3">
              Sending as {{ user?.name }} ({{ user?.email }})
            </p>
            <p v-else class="text-xs text-gray-400 text-center mt-3">
              We'll reply to the email address you provide.
            </p>
          </template>
        </div>
      </div>
    </Transition>

    <!-- Backdrop (click to close) -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-40"
        @click="close"
      />
    </Transition>
  </Teleport>
</template>
